import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/meta';
import { gpcActive, openConsentManager } from '../lib/consent';
import { CONTACT_EMAIL } from '../config';

export function PrivacySettingsPage() {
  usePageMeta({
    title: 'Privacy Settings',
    description:
      'Manage your advertising consent choices, opt out of the sale or sharing of personal information, and check your Global Privacy Control status.',
    path: '/privacy-settings',
  });

  const [cmpMissing, setCmpMissing] = useState(false);
  const gpc = gpcActive();

  const handleOpenConsent = () => {
    const opened = openConsentManager();
    setCmpMissing(!opened);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-3xl text-plume-950 sm:text-5xl">Privacy Settings</h1>
      <p className="mt-6 text-lg leading-relaxed text-plume-900">
        This page is the single place to review or change every privacy choice that applies to this
        site. It also serves as our &ldquo;Do Not Sell or Share My Personal Information&rdquo; page
        for US state privacy laws.
      </p>

      <section className="mt-10 rounded-2xl bg-white p-6 shadow-gloss">
        <h2 className="font-display text-2xl text-plume-950">Advertising consent</h2>
        <p className="mt-3 leading-relaxed text-plume-800">
          Ad consent on this site is managed by Google&rsquo;s certified consent platform. In the
          EEA, UK, and Switzerland you were shown a consent message before any advertising storage
          was used; elsewhere, ads run on an opt-out basis. Use the button below to reopen the
          consent manager and review, change, or withdraw your choices at any time.
        </p>
        <button type="button" onClick={handleOpenConsent} className="btn-gloss mt-5">
          Manage my consent choices
        </button>
        {cmpMissing && (
          <p role="status" className="mt-4 rounded-xl bg-plume-50 p-4 text-sm text-plume-800">
            The consent manager is not active in this build: advertising is currently disabled
            site-wide, so there are no ad cookies to manage. If you believe you are seeing ads
            anyway, please email{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        )}
      </section>

      <section className="mt-6 rounded-2xl bg-white p-6 shadow-gloss">
        <h2 className="font-display text-2xl text-plume-950">Global Privacy Control</h2>
        <p className="mt-3 leading-relaxed text-plume-800">
          We honor the GPC browser signal as a binding opt-out of the sale or sharing of personal
          information, in every region.
        </p>
        <p className="mt-4">
          <span
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
              gpc ? 'bg-tail-100 text-tail-800' : 'bg-plume-50 text-plume-800'
            }`}
          >
            <span
              aria-hidden="true"
              className={`h-2.5 w-2.5 rounded-full ${gpc ? 'bg-tail-600' : 'bg-plume-400'}`}
            />
            {gpc
              ? 'GPC detected: your browser is sending the opt-out signal, and we are honoring it.'
              : 'GPC not detected in this browser.'}
          </span>
        </p>
        {!gpc && (
          <p className="mt-3 text-sm leading-relaxed text-plume-700">
            You can enable GPC through your browser&rsquo;s privacy settings or an extension; see{' '}
            <a
              href="https://globalprivacycontrol.org"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4"
            >
              globalprivacycontrol.org
            </a>
            .
          </p>
        )}
      </section>

      <section className="mt-6 rounded-2xl bg-white p-6 shadow-gloss">
        <h2 className="font-display text-2xl text-plume-950">Other controls</h2>
        <ul className="mt-3 list-disc space-y-2 pl-6 leading-relaxed text-plume-800">
          <li>
            Google ad personalization:{' '}
            <a
              href="https://adssettings.google.com"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4"
            >
              adssettings.google.com
            </a>
          </li>
          <li>
            Industry-wide opt-out:{' '}
            <a
              href="https://optout.aboutads.info"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4"
            >
              optout.aboutads.info
            </a>
          </li>
          <li>
            Details on every category of cookie in use: our{' '}
            <Link to="/cookies" className="font-semibold underline underline-offset-4">
              Cookie Policy
            </Link>
          </li>
          <li>
            Data rights requests and anything else:{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
          </li>
        </ul>
        <p className="mt-4 text-sm leading-relaxed text-plume-700">
          This site has no accounts, forms, or first-party tracking, so there is no site-held
          personal data to access or delete; requests about ad data are handled through the Google
          tools above, and we are glad to help by email.
        </p>
      </section>
    </div>
  );
}
